import { createMultiPartRequest, createRequest } from '../helpers/api_utils';
import { workArrangement } from '../types/JobOpenings';
import { apiUrl } from './api_url';

const apiEndpoint = apiUrl + '/job-posting';

export async function createJobPost(body: { jobOpeningId: string, department: string, jobTitle: string, workType: workArrangement, jsonTemplate: string, htmlTemplate: string, startDate: string, endDate: string, active: boolean, location: string }) {
  return fetch(createRequest(apiEndpoint, 'POST', 'application/json', true, body));
}

export async function getJobPost(id: string, lang?: string) {
  const url = lang ? apiEndpoint + `/${id}?lang=${encodeURIComponent(lang)}` : apiEndpoint + `/${id}`;
  return fetch(createRequest(url, 'GET', 'application/json', true));
}

export function getJobPostList(job_title: string[] = [], location: string[] = [], department: string[] = [], workType: string[] = [], page: number = 1, limit: number = 10, lang?: string) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());

  if (job_title.length > 0) params.append('job_title', job_title.join(','));
  if (location.length > 0) params.append('location', location.join(','));
  if (department.length > 0) params.append('department', department.join(','));
  if (workType.length > 0) params.append('work_type', workType.join(','));
  if (lang) params.append('lang', lang);

  const url = `${apiEndpoint}/list?${params.toString()}`;
  return fetch(createRequest(url, 'GET', 'application/json', true));
}

export function getJobFilters(lang?: string) {
  const url = lang
    ? apiEndpoint + `/search/filters?lang=${encodeURIComponent(lang)}`
    : apiEndpoint + '/search/filters';
  return fetch(createRequest(url, 'GET', 'application/json', true));
}

export async function suggestTranslation(id: string, targetLanguage: string) {
  const formData = new FormData();
  formData.append('target_language', targetLanguage);
  return fetch(createMultiPartRequest(apiEndpoint + `/${id}/translate`, 'POST', true, formData));
}

export async function saveTranslation(id: string, lang: string, data: { jsonTemplate: string; htmlTemplate: string }) {
  const formData = new FormData();
  formData.append('json_template', data.jsonTemplate);
  formData.append('html_template', data.htmlTemplate);
  return fetch(createMultiPartRequest(apiEndpoint + `/${id}/translations/${encodeURIComponent(lang)}`, 'PUT', true, formData));
}

export async function deleteTranslation(id: string, lang: string) {
  return fetch(createRequest(apiEndpoint + `/${id}/translations/${encodeURIComponent(lang)}`, 'DELETE', 'application/json', true));
}

export async function applyJob(
  id: string,
  documents: { file: File; type: string }[],
) {
  let formData = new FormData();
  documents.forEach((doc) => {
    formData.append(doc.type, doc.file);
  });

  return fetch(
    createMultiPartRequest(
      apiEndpoint + `/${id}/apply`,
      'POST',
      true,
      formData,
    ),
  );
}

export async function createJobPostUpload( jobOpeningId: string, department: string, jobTitle: string, workType: workArrangement, jsonTemplate: string, htmlTemplate: string, startDate: string, endDate: string, active: boolean, location: string, videoFile?: File, videoDescription?: string, videoTitle?: string ) {
  let formData = new FormData();
  formData.append('job_opening_id', jobOpeningId);
  formData.append('department', department );
  formData.append('work_type', workType);
  formData.append('job_title', jobTitle);
  formData.append('html_template', htmlTemplate);
  formData.append('json_template', jsonTemplate);
  formData.append('start_date', startDate);
  formData.append('end_date', endDate);
  formData.append('active', active.toString());
  formData.append('location', location);
  if (videoFile) {
    formData.append('file', videoFile);
  }
  if (videoDescription && videoTitle) {
    formData.append('video_description', videoDescription);
    formData.append('video_title', videoTitle);
  }

  return fetch(createMultiPartRequest(apiEndpoint, 'POST', true, formData));
}

export async function updateJobPostUpload( jobPostingId: string, jobOpeningId: string, department: string, jobTitle: string, workType: workArrangement, jsonTemplate: string, htmlTemplate: string, startDate: string, endDate: string, active: boolean, location: string, videoFile?: File, videoDescription?: string, videoTitle?: string ) {
  let formData = new FormData();
  formData.append('job_opening_id', jobOpeningId);
  formData.append('department', department );
  formData.append('work_type', workType);
  formData.append('job_title', jobTitle);
  formData.append('html_template', htmlTemplate);
  formData.append('json_template', jsonTemplate);
  formData.append('start_date', startDate);
  formData.append('end_date', endDate);
  formData.append('active', active.toString());
  formData.append('location', location);
  if (videoFile) {
    formData.append('file', videoFile);
  }
  if (videoDescription && videoTitle) {
    formData.append('video_description', videoDescription);
    formData.append('video_title', videoTitle);
  }

  return fetch(createMultiPartRequest(apiEndpoint + `/${jobPostingId}`, 'PUT', true, formData));
}

export function shareJobWithFriend(
  friendEmail: string,
  jobUrl: string,
  jobTitle?: string,
  companyName?: string,
) {
  // Simple mailto implementation - opens default email client with pre-filled job details
  // Perfect for public job posts from different tenants (no backend email needed)

  const jobTitleText = jobTitle || 'Job Opportunity';
  const companyText = companyName || 'Company';

  // Create professional email content
  const subject = encodeURIComponent(
    `Check out this job: ${jobTitleText} at ${companyText}`,
  );
  const body = encodeURIComponent(
    'Hey,\n\n' +
      'I found this job opportunity and thought of you:\n\n' +
      `Position: ${jobTitleText}\n` +
      `Company: ${companyText}\n` +
      `Apply here: ${jobUrl}\n\n` +
      'This looks like it could be a great fit for your skills and experience. ' +
      'The role seems interesting and the company has good opportunities.\n\n' +
      'Let me know what you think!\n\n' +
      'Best regards',
  );

  // Create mailto URL with recipient if provided, otherwise leave it empty for user to fill
  const mailtoUrl = friendEmail
    ? `mailto:${friendEmail}?subject=${subject}&body=${body}`
    : `mailto:?subject=${subject}&body=${body}`;

  // Open default email client
  window.open(mailtoUrl, '_blank');

  // Return success response in expected format
  return Promise.resolve({
    ok: true,
    json: () =>
      Promise.resolve({
        message:
          'Email client opened successfully. Please complete sending the email.',
      }),
  });
}
